import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import api from '../lib/api';
import { useI18n } from '../i18n/I18nContext';
import { useAuth, homeFor } from '../context/AuthContext';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Volleyball, ShieldCheck } from 'lucide-react';

const inputCls = "h-14 rounded-2xl text-base bg-nuit-900 border-nuit-600 text-creme placeholder:text-brume focus:ring-4 focus:ring-or-400/40";

export default function RegisterAdult() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get('code') || '');
  const [form, setForm] = useState({ name: '', email: '' });
  const [step, setStep] = useState('form');
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useI18n();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/register-adult', { ...form, code });
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/auth/register-adult/verify', { email: form.email, code: otp.trim() });
      login(data.token, data.user);
      toast.success(t('registerAdult.created', { role: data.user.role === 'educateur' ? t('registerAdult.roleEducateur') : t(`roles.${data.user.role}`, undefined, data.user.role) }));
      navigate(homeFor(data.user.role));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const brand = (
    <div className="flex items-center justify-between gap-2 mb-8">
      <div className="flex items-center gap-2 font-fredoka font-semibold text-lg text-or-400">
        <span className="w-9 h-9 rounded-full bg-or-400 flex items-center justify-center">
          <Volleyball className="w-5 h-5 text-nuit-950" strokeWidth={2.5} />
        </span>
        {t('registerAdult.title')}
      </div>
      <LanguageSwitcher />
    </div>
  );

  if (step === 'otp') {
    return (
      <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
          className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
          data-testid="register-adult-verify-step">
          {brand}
          <div className="text-center mb-6">
            <span className="w-16 h-16 rounded-full bg-or-400/15 flex items-center justify-center mx-auto mb-4">
              <ShieldCheck className="w-8 h-8 text-or-400" strokeWidth={2.5} />
            </span>
            <h1 className="text-2xl font-bold text-creme mb-2">{t('registerAdult.otpTitle')}</h1>
            <p className="text-sm text-brume leading-relaxed">{t('login.codeSentTo', { email: form.email })}</p>
          </div>
          <form onSubmit={verify} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-creme mb-2" htmlFor="adult-otp">{t('register.otpLabel')}</label>
              <Input id="adult-otp" data-testid="adult-otp-input" required inputMode="numeric" maxLength={6}
                value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))} placeholder="••••••"
                className={`${inputCls} text-center text-2xl tracking-[0.5em] font-bold`} />
            </div>
            <Button type="submit" data-testid="adult-otp-submit" disabled={loading || otp.length !== 6}
              className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
              {loading ? t('common.sending') : t('register.otpSubmit')}
            </Button>
            <p className="text-center text-sm text-brume">
              <button type="button" onClick={() => setStep('form')} data-testid="adult-otp-back"
                className="text-or-400 font-bold hover:underline">{t('register.otpBack')}</button>
            </p>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-nuit-900 flex items-center justify-center px-4 py-12">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        className="bg-nuit-800 border border-nuit-700 rounded-3xl p-8 sm:p-10 max-w-md w-full gold-glow"
        data-testid="register-adult-page">
        {brand}
        <form onSubmit={submit} className="space-y-5">
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="adult-code">{t('register.codeLabel')}</label>
            <Input id="adult-code" data-testid="adult-code-input" required value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={t('landing.codePlaceholder')}
              className={`${inputCls} font-mono tracking-widest`} />
          </div>
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="adult-name">{t('registerAdult.name')}</label>
            <Input id="adult-name" data-testid="adult-name-input" required value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('registerAdult.namePlaceholder')}
              className={inputCls} />
          </div>
          <div>
            <label className="block text-sm font-bold text-creme mb-2" htmlFor="adult-email">{t('registerAdult.email')}</label>
            <Input id="adult-email" data-testid="adult-email-input" required type="email" value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={t('registerAdult.emailPlaceholder')}
              className={inputCls} />
          </div>
          <p className="text-xs text-brume leading-relaxed">
            {t('registerAdult.note')}
          </p>
          <Button type="submit" data-testid="register-adult-submit"
            disabled={loading || !code.trim() || !form.name.trim() || !form.email.includes('@')}
            className="w-full h-14 rounded-full bg-or-400 hover:bg-or-300 text-nuit-950 font-bold text-base shadow-md hover:-translate-y-0.5 transition-transform">
            {loading ? t('common.sending') : t('registerAdult.submit')}
          </Button>
          <p className="text-center text-sm text-brume">
            <Link to="/" className="text-or-400 font-bold hover:underline" data-testid="adult-back-link">{t('common.back')}</Link>
          </p>
        </form>
      </motion.div>
    </div>
  );
}
